/**
 * Auth Service
 * Handles user authentication, session management, and profile operations
 * 
 * Uses profileRepository for data access and returns ServiceResult<T> for all operations.
 * 
 * @module services/authService
 */

import { supabase } from './supabase';
import { UserProfile } from '../types';
import { profileRepository, ProfileRow, ProfileInsert } from '../repositories/profileRepository';
import { ServiceResult, ok, err } from '../types/api/result';
import { AppError } from '../lib/errors/AppError';
import { logger } from '../lib/logger';

// =============================================================================
// Constants
// =============================================================================

const PROFILE_KEY = 'notespace_profile_v1';

// =============================================================================
// State Management
// =============================================================================

// Cached current user to avoid hanging getUser calls
let cachedUserId: string | null = null;

/**
 * Set the cached user ID
 */
export const setCurrentUserId = (userId: string | null): void => {
    cachedUserId = userId;
    logger.debug('[AuthService] userId set', { userId: userId ? 'set' : 'cleared' });
};

/**
 * Get the cached user ID
 */
export const getCurrentUserId = (): string | null => cachedUserId;

// =============================================================================
// Profile Helpers
// =============================================================================

/**
 * Transform database row to UserProfile domain object
 */
function toUserProfile(row: ProfileRow): UserProfile {
    const defaultName = row.email ? row.email.split('@')[0] : 'User';
    return {
        id: row.id,
        name: row.full_name || row.username || defaultName,
        avatar: row.avatar_url ?? undefined,
        email: row.email ?? undefined,
    };
}

/**
 * Cache profile to localStorage
 */
function cacheProfile(profile: UserProfile): void {
    try {
        localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
    } catch (error) {
        logger.warn('[AuthService] Failed to cache profile', error);
    }
}

/**
 * Get cached profile from localStorage
 */
export function getCachedProfile(): UserProfile | null {
    try {
        const saved = localStorage.getItem(PROFILE_KEY);
        return saved ? JSON.parse(saved) : null;
    } catch {
        return null;
    }
}

/**
 * Clear cached profile
 */
function clearCachedProfile(): void {
    try {
        localStorage.removeItem(PROFILE_KEY);
    } catch (error) {
        logger.warn('[AuthService] Failed to clear cached profile', error);
    }
}

// =============================================================================
// Service Operations
// =============================================================================

/**
 * Fetch user profile by ID
 * Uses profileRepository for data access
 */
export async function fetchProfile(userId: string): Promise<ServiceResult<UserProfile>> {
    try {
        logger.debug('[AuthService] fetchProfile', { userId });

        let row = await profileRepository.findById(userId);

        // Self-healing: If profile missing or incomplete, try to sync from Auth
        if (!row || !row.email || !row.full_name) {
            const { data: { user } } = await supabase.auth.getUser();

            // Only sync if we are fetching the current authenticated user
            if (user && user.id === userId) {
                const email = user.email;
                const metaName = user.user_metadata?.full_name || user.user_metadata?.name;
                const metaAvatar = user.user_metadata?.avatar_url;
                const defaultName = email ? email.split('@')[0] : undefined;
                const nameToUse = metaName || defaultName;

                if (!row) {
                    // Create missing profile
                    try {
                        logger.info('[AuthService] Self-healing: Creating missing profile');
                        const newProfile: ProfileInsert = {
                            id: userId,
                            email: email,
                            full_name: nameToUse,
                            avatar_url: metaAvatar
                        };
                        row = await profileRepository.create(newProfile);
                    } catch (createErr) {
                        logger.warn('[AuthService] Failed to create profile during self-heal', createErr);
                    }
                } else {
                    // Update incomplete profile
                    // Only update if we have better data
                    const updates: Record<string, string> = {};
                    if (!row.email && email) updates.email = email;
                    if (!row.full_name && nameToUse) updates.full_name = nameToUse;

                    if (Object.keys(updates).length > 0) {
                        try {
                            logger.info('[AuthService] Self-healing: Updating incomplete profile', updates);
                            row = await profileRepository.update(userId, updates);
                        } catch (updateErr) {
                            logger.warn('[AuthService] Failed to update profile during self-heal', updateErr);
                        }
                    }
                }
            }
        }

        if (!row) {
            return err(new AppError('Profile not found', {
                code: 'PROFILE_NOT_FOUND',
                statusCode: 404,
                context: { userId }
            }));
        }

        const profile = toUserProfile(row);
        cacheProfile(profile);

        return ok(profile);
    } catch (error) {
        logger.error('[AuthService] fetchProfile failed', error);
        return err(AppError.from(error, { context: { userId } }));
    }
}

/**
 * Update user profile
 * Uses profileRepository for data access
 */
export async function updateProfile(
    userId: string,
    updates: Partial<UserProfile>
): Promise<ServiceResult<UserProfile>> {
    try {
        logger.debug('[AuthService] updateProfile', { userId, fields: Object.keys(updates) });

        // Transform UserProfile fields to database fields
        const payload: { full_name?: string; avatar_url?: string; email?: string } = {};
        if (updates.name) payload.full_name = updates.name;
        if (updates.avatar) payload.avatar_url = updates.avatar;
        if (updates.email) payload.email = updates.email;

        // Update via repository
        const row = await profileRepository.update(userId, payload);
        const profile = toUserProfile(row);

        // Update local cache
        cacheProfile(profile);

        return ok(profile);
    } catch (error) {
        logger.error('[AuthService] updateProfile failed', error);
        return err(AppError.from(error, { context: { userId } }));
    }
}

/**
 * Sign out the current user
 */
export async function signOut(): Promise<ServiceResult<void>> {
    try {
        logger.debug('[AuthService] signOut');

        await supabase.auth.signOut();

        // Clear all cached state
        setCurrentUserId(null);
        clearCachedProfile();

        return ok(undefined);
    } catch (error) {
        logger.error('[AuthService] signOut failed', error);

        // Still clear local state even if Supabase call fails
        setCurrentUserId(null);
        clearCachedProfile();

        return err(AppError.from(error));
    }
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
    return cachedUserId !== null;
}

// End of Service Operations
